import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { CircleCheck as CheckCircle, ArrowRight, Phone, Target, Briefcase, Heart, Users } from 'lucide-react';

const UeberMich = () => {
  const seoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Stephan Schunter',
    jobTitle: 'IT-Dienstleister',
    description: 'Stephan Schunter – IT-Dienstleister für KMU in Mönchengladbach. Pragmatisch, ehrlich, ohne Marketing-Blabla. Microsoft 365, Digitalisierung, IT-Sicherheit.',
    url: 'https://schteffann.de/ueber-mich',
    worksFor: { '@type': 'LocalBusiness', name: 'Schteffann', url: 'https://schteffann.de/' }
  };

  const werte = [
    {
      icon: Heart,
      title: 'Ehrlich und direkt',
      text: 'Ich sage Ihnen, was Sinn macht – und was nicht. Keine unnötigen Projekte, keine Marketing-Phrasen.'
    },
    {
      icon: Users,
      title: 'KMU-tauglich',
      text: 'Ich kenne den Alltag in kleinen Unternehmen. Lösungen müssen praktikabel sein, nicht perfekt.'
    },
    {
      icon: Briefcase,
      title: 'Langfristig gedacht',
      text: 'Ich verkaufe keine Quick-Fixes. Mein Ziel ist, dass Ihre IT nachhaltig funktioniert.'
    }
  ];

  const erfahrung = [
    'Über 15 Jahre Erfahrung in der IT-Administration',
    'Spezialisierung auf Microsoft 365 und Cloud-Lösungen',
    'Arbeit mit KMU aus Handwerk, Dienstleistung und Büros',
    'Fokus auf Sicherheit, Prozesse und praktische Digitalisierung',
    'Zertifizierungen in IT-Sicherheit und Microsoft-Technologien'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEOHead
        title="Über mich – Stephan Schunter | Schteffann IT-Dienstleistungen Mönchengladbach"
        description="Stephan Schunter – IT-Dienstleister für KMU in Mönchengladbach. Pragmatisch, ehrlich, ohne Marketing-Blabla. Microsoft 365, Digitalisierung, IT-Sicherheit."
        keywords="Stephan Schunter, IT-Dienstleister Mönchengladbach, Microsoft 365 Spezialist, IT KMU, Schteffann, Viersen, Krefeld"
        jsonLd={seoJsonLd}
      />
      {/* Hero Section */}
      <section className="relative py-20 lg:py-24 overflow-hidden bg-gradient-to-br from-cyan-50/50 via-blue-50/30 to-slate-50">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-600/5"></div>
        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-cyan-200 px-6 py-2">
              Über mich
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-slate-900">Hallo, ich bin </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700">Stephan Schunter</span>
            </h1>
            <p className="text-xl lg:text-2xl text-slate-600 leading-relaxed">
              IT-Dienstleister für kleine und mittlere Unternehmen in Mönchengladbach und Umgebung.
            </p>
          </div>
        </div>
      </section>

      {/* Haupttext */}
      <section className="py-20">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Warum ich mache, was ich mache</h2>
              
              <p className="text-lg text-slate-700 mb-6 leading-relaxed">
                Ich arbeite seit über 15 Jahren in der IT – als Administrator, Projektleiter und Berater. In dieser Zeit habe ich vor allem eines gelernt: Die meisten Unternehmen brauchen keine hochkomplexen IT-Lösungen. Sie brauchen etwas, das funktioniert, verständlich ist und nicht ständig Probleme macht.
              </p>

              <p className="text-lg text-slate-700 mb-6 leading-relaxed">
                Viele KMU haben Microsoft 365 – aber nutzen nur einen Bruchteil der Möglichkeiten. Oder sie haben IT-Systeme, die irgendwann mal eingerichtet wurden, aber niemand hat sie seitdem wirklich angepasst. Genau da setze ich an.
              </p>

              <p className="text-lg text-slate-700 mb-6 leading-relaxed">
                Mein Ansatz ist pragmatisch: Ich schaue mir an, wo Sie stehen, was Sie brauchen und was realistisch umsetzbar ist. Keine großen Versprechen, keine unnötigen Projekte. Nur ehrliche Beratung und praktische Lösungen.
              </p>

              <p className="text-lg text-slate-700 leading-relaxed">
                Ich arbeite hauptsächlich mit Unternehmen aus Handwerk, Dienstleistung und kleinen Büros – also mit Leuten, die keine riesige IT-Abteilung haben und trotzdem eine vernünftige IT brauchen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Werte */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-cyan-50/30">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Wie ich arbeite
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {werte.map((item, index) => (
              <Card key={index} className="bg-white border-slate-200 shadow-sm">
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-2xl mb-6">
                    <item.icon className="h-8 w-8 text-cyan-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{item.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Erfahrung */}
      <section className="py-20">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Meine Erfahrung im Überblick</h2>
            <Card className="border-slate-200 shadow-lg">
              <CardContent className="p-8">
                <div className="space-y-4">
                  {erfahrung.map((item, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                      <p className="text-lg text-slate-700">{item}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container">
          <Card className="bg-gradient-to-br from-cyan-600 via-blue-600 to-cyan-700 border-0 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
            <CardContent className="p-12 lg:p-16 relative text-white text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Lassen Sie uns reden
              </h2>
              <p className="text-xl text-cyan-100 mb-10 max-w-2xl mx-auto">
                In einem kostenlosen Erstgespräch schaue ich mir Ihre Situation an und sage Ihnen ehrlich, was ich tun kann – und was nicht.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Button size="lg" asChild className="bg-white text-cyan-700 hover:bg-cyan-50 shadow-xl px-8 py-4 text-lg">
                  <Link to="/contact">
                    <Target className="mr-2 h-5 w-5" />
                    Kontakt aufnehmen
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg">
                  <a href="tel:015679697252">
                    <Phone className="mr-2 h-5 w-5" />
                    0156-79697252
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default UeberMich;